// ---- Humanoid face (expressions) ----
// The eyes / brows / mouth that ride the egg head, extracted from humanoid.ts so
// the one face can wear several EXPRESSIONS (shared.ts FACE_EXPRESSIONS). Each
// expression is a small pose over the shared face tuning (HUMANOID_TUNING.face,
// passed in as `F` to keep this module free of a humanoid.ts import): where the
// brows tilt/sit, whether the eyes are round / wide / squinted, and which mouth
// curve is drawn. "happy" reproduces the historical face BYTE-IDENTICALLY (same
// prims, same numbers), so the model snapshot baseline is untouched — only the
// non-default expressions change geometry.
//
// "talking" is the one animated expression: its mouth is emitted inside a
// `limb("mouth")` pivot group so gait.ts can oscillate the group's scale.y each
// frame (animateFaceMouth) into a jabbering open/close — reusing the same limb
// collection + invalidation the walk cycle already runs, so no new per-frame
// bookkeeping. DOM-free / THREE-only, same portability contract as humanoid.ts.

import type * as THREE from "three";
import type { Prims } from "../meshkit.ts";
import { shade } from "../color.ts";
import type { FaceExpression } from "../shared.ts";

/** The face knobs buildHumanoidFace reads — structurally HUMANOID_TUNING.face
 *  (humanoid.ts owns the values; passing them keeps this module acyclic). */
export type FaceParams = {
  eyeX: number; eyeY: number;
  scleraR: number; scleraZ: number; scleraDepth: number;
  irisR: number; irisZ: number; irisDepth: number;
  glintR: number; glintZ: number; glintUp: number; glintIn: number;
  yaw: number;
  browY: number; browZ: number; browW: number; browH: number; browD: number;
  browTilt: number;
  mouthY: number; mouthZ: number; mouthRadius: number; mouthTube: number; mouthSweep: number;
};

/** How each eye reads: round pupil-in-white (default), a wide gasp, or a
 *  squinted horizontal dash (the "-.-" annoyed slit). */
type EyeStyle = "open" | "wide" | "slit";
/** Which mouth curve is drawn (talk = the animated open/close cavity). */
type MouthStyle = "smile" | "frown" | "flat" | "gasp" | "talk";

type FacePose = {
  /** Per-side brow rot.z (× sx). Negative = inner ends UP (relaxed / worried),
   *  positive = inner ends DOWN (the stern/angry tell). */
  browTilt: number;
  /** Added to browY (local units, ×s): + raises the brows (surprise), − hoods
   *  them down over the eyes (anger / annoyance). */
  browRaise: number;
  eye: EyeStyle;
  /** × the sclera/iris radius (wide-eyed surprise pushes this up). */
  eyeScale: number;
  mouth: MouthStyle;
};

/** The expression table — a pose per shared.ts FACE_EXPRESSIONS entry. Eyeball-
 *  tuned; the user verifies in the studio (Archetype → Humanoid → Expression).
 *  "happy" MUST stay equal to the historical face (browTilt = F.browTilt via 0
 *  delta below, no raise, round eyes, smile) so the snapshot baseline holds. */
const FACE_POSES: Record<FaceExpression, FacePose> = {
  // browTilt here is the ABSOLUTE per-side value; happy carries the historical
  // F.browTilt (-0.04) so it round-trips identically.
  happy: { browTilt: -0.04, browRaise: 0, eye: "open", eyeScale: 1, mouth: "smile" },
  // Inner brows lifted + pulled up, sitting low, a soft downturned mouth.
  sad: { browTilt: -0.34, browRaise: -0.008, eye: "open", eyeScale: 1, mouth: "frown" },
  // Hard inner-down brows raised off the eyes, a tight flat grimace.
  angry: { browTilt: 0.42, browRaise: -0.01, eye: "open", eyeScale: 0.94, mouth: "flat" },
  // Brows shot up + level, wide eyes, an open gasping "O".
  surprised: { browTilt: 0.0, browRaise: 0.06, eye: "wide", eyeScale: 1.32, mouth: "gasp" },
  // Half-lidded dash eyes, low flat brows, a small unimpressed line ( -.- ).
  annoyed: { browTilt: 0.08, browRaise: -0.02, eye: "slit", eyeScale: 1, mouth: "flat" },
  // Neutral-engaged brows; the mouth is the animated open/close cavity.
  talking: { browTilt: -0.02, browRaise: 0.004, eye: "open", eyeScale: 1, mouth: "talk" },
};

export type FaceOptions = {
  headY: number;
  /** Body scale (the 1.1 base × doc scale) — every face dim is ×s. */
  s: number;
  F: FaceParams;
  /** Iris / dash color. */
  eye: number;
  /** Brow bar color (hair, or a shade of skin). */
  browColor: number;
  /** Skin — the mouth line/cavity are shades of it. */
  skin: number;
  expression: FaceExpression;
};

/** Build the eyes + brows + mouth for one expression. Emitted at the same
 *  head-local spots the inline humanoid face used, so it drops straight into the
 *  `head` limb. For "talking" the mouth is wrapped in a `limb("mouth")` group for
 *  animateFaceMouth to drive. */
export function buildHumanoidFace(
  p: Pick<Prims, "box" | "ball" | "arc" | "limb">,
  o: FaceOptions,
): void {
  const { headY, s, F, eye, browColor, skin, expression } = o;
  const pose = FACE_POSES[expression];
  const lip = shade(skin, -0.28); // the historical smile/lip line tone
  const cavity = shade(skin, -0.62); // the dark inside of an open mouth

  for (const sx of [-1, 1]) {
    const yaw = [0, sx * F.yaw, 0] as const;
    const ex = sx * F.eyeX * s;
    const ey = headY + F.eyeY * s;
    if (pose.eye === "slit") {
      // Squinted dash — a thin dark horizontal bar where the eye would be (the
      // "-.-" tell). No sclera/iris; the bar reads as a closed, unamused lid.
      p.box(ex, ey, F.scleraZ * s, F.scleraR * 2.1 * s, 0.008 * s, 0.024 * s, eye, yaw);
    } else {
      const er = pose.eyeScale;
      // Round eye: white sclera flattened into the egg, a small dark iris proud
      // of it, a tiny catchlight up-and-in — a soft, alive gaze (wide-eyed for
      // surprise via the eyeScale multiplier).
      p.ball(ex, ey, F.scleraZ * s, F.scleraR * er * s, 0xf4efe6, F.scleraDepth, yaw); // sclera
      p.ball(ex, ey, F.irisZ * s, F.irisR * er * s, eye, F.irisDepth, yaw); // iris
      p.ball(ex - sx * F.glintIn * s, headY + (F.eyeY + F.glintUp) * s, F.glintZ * s, F.glintR * s, 0xfffdf5); // catchlight
    }
    // Brow: thin bar, tilted per the pose (inner up/down) and raised/lowered.
    p.box(ex, headY + (F.browY + pose.browRaise) * s, F.browZ * s, F.browW * s, F.browH * s, F.browD * s, browColor, [0, sx * F.yaw, sx * pose.browTilt]);
  }

  // Mouth. smile = the historical arc; frown flips it down; flat is a tense
  // line; gasp is a dark open O; talk wraps a dark cavity in a limb the gait
  // animator opens/closes.
  const mz = F.mouthZ * s;
  switch (pose.mouth) {
    case "smile":
      p.arc(0, headY + F.mouthY * s, mz, F.mouthRadius * s, F.mouthTube * s, lip, F.mouthSweep);
      break;
    case "frown": {
      // A deeper downturn (bigger sweep). rot.z = π flips the arc so the corners
      // hang below the center; the arc pivots about its anchor, so anchor a
      // little above F.mouthY to keep the frown centered on the mouth.
      const radius = 0.115 * s, sweep = 1.0;
      p.arc(0, headY + (F.mouthY + 0.02) * s, mz, radius, F.mouthTube * s, lip, sweep, [0, 0, Math.PI]);
      break;
    }
    case "flat":
      // Straight, faintly-rounded lip line — a tense/unimpressed mouth.
      p.box(0, headY + F.mouthY * s, mz, 0.088 * s, 0.009 * s, 0.02 * s, lip);
      break;
    case "gasp":
      // Dropped-open round mouth (a small gasp), sitting a touch lower.
      p.ball(0, headY + (F.mouthY - 0.012) * s, mz, 0.03 * s, cavity, 0.42);
      break;
    case "talk": {
      // The animated cavity: a wide dark ellipse in its own pivot group so
      // gait.ts (animateFaceMouth) can scale it open/closed each frame. Built at
      // a mid-open size; scale.y rides ~0.25 (a line) … ~1.1 (agape).
      const my = headY + F.mouthY * s;
      // Own pivot group named "mouth" so gait.ts (animateFaceMouth) finds it in
      // the limb map and scales it open/closed. The limb NAME is the contract.
      p.limb("mouth", [0, my, mz], () => {
        p.ball(0, my, mz, 0.032 * s, cavity, 0.4);
      });
      break;
    }
  }
}

/** Open/close a talking mouth for one frame. `mouth` is the `limb("mouth")`
 *  group (found in the gait limb map for a "talking" face); `time` is the shared
 *  clock. A two-harmonic jabber that never fully seals, so it reads as speech,
 *  not a chewing snap. No-op for every other expression (there's no "mouth"
 *  limb). */
export function animateFaceMouth(mouth: THREE.Group, time: number): void {
  // Base fast open/close + a slower amplitude wobble so the cadence isn't a
  // perfect metronome; clamp to a mostly-open, never-shut range.
  const fast = 0.5 + 0.5 * Math.sin(time * 12);
  const slow = 0.5 + 0.5 * Math.sin(time * 3.3 + 1.7);
  mouth.scale.y = 0.25 + (0.55 + 0.35 * slow) * fast;
}
